/**
 * Account preferences (registry identity-workspace.preferences.*; architecture §2.7 `user_settings`, §3.11).
 *
 * Shared by the client (selectors, the Preferences page) and the server (validation hooks, assign-on-start). The
 * columns that already exist (`show_full_user_names`, `auto_assign_to_self`, `default_home_view`, `font_size`,
 * `theme`) are read as they are. The preferences the draft schema did not have (first day of week, emoticons,
 * comment submit key, assign on start, pointer cursors, underline links) live as typed keys of
 * `user_settings.ui_prefs`, which is written with per-key merge (`{ set, unset }`), so two tabs changing different
 * preferences never clobber each other and no migration is needed. Every reader goes through `readPreferences`,
 * which falls back to the default for a missing or malformed key.
 */
import { z } from 'zod';

export type FirstDayOfWeek = 0 | 1;
export type CommentSubmitKey = 'enter' | 'mod-enter';
export type FontSize = 'small' | 'default' | 'large';

/** Preferences stored as `ui_prefs` keys, with their defaults (registry ac_na: every one always has a value). */
export interface UiPrefPreferences {
  /** 0 = Sunday (default, registry first-day-of-week AC2), 1 = Monday. */
  firstDayOfWeek: FirstDayOfWeek;
  /** Convert text emoticons into emoji (default on, editor-input AC1). */
  convertEmoticons: boolean;
  /** Comment submit key (default Cmd/Ctrl+Enter, editor-input AC3). */
  commentSubmit: CommentSubmitKey;
  /** On move to a started status, assign to yourself (default off, auto-assign AC3). */
  assignOnStart: boolean;
  /** Use pointer cursors (default off, pointer-cursors AC1). */
  pointerCursors: boolean;
  /** Underline links in content (default off, pointer-cursors-underline-links AC2). */
  underlineLinks: boolean;
}

export const UI_PREF_DEFAULTS: Readonly<UiPrefPreferences> = Object.freeze({
  firstDayOfWeek: 0,
  convertEmoticons: true,
  commentSubmit: 'mod-enter',
  assignOnStart: false,
  pointerCursors: false,
  underlineLinks: false,
});

export type UiPrefKey = keyof UiPrefPreferences;

/** The schema of each preference key inside `ui_prefs` (server validation and client parsing). */
export const UI_PREF_SCHEMAS: { readonly [K in UiPrefKey]: z.ZodType<UiPrefPreferences[K]> } = {
  firstDayOfWeek: z.union([z.literal(0), z.literal(1)]),
  convertEmoticons: z.boolean(),
  commentSubmit: z.enum(['enter', 'mod-enter']),
  assignOnStart: z.boolean(),
  pointerCursors: z.boolean(),
  underlineLinks: z.boolean(),
};

export const UI_PREF_KEYS = Object.keys(UI_PREF_DEFAULTS) as readonly UiPrefKey[];

export function isUiPrefKey(k: string): k is UiPrefKey {
  return (UI_PREF_KEYS as readonly string[]).includes(k);
}

/** Every account preference, resolved with defaults. */
export interface Preferences extends UiPrefPreferences {
  /**
   * Display full names instead of usernames (`show_full_user_names`). On for new users (display-full-names AC1); the
   * column default flips to true in a migration (requested), and existing rows keep their stored value (AC6).
   */
  showFullUserNames: boolean;
  /** Auto-assign issues you create to yourself (`auto_assign_to_self`). */
  autoAssignToSelf: boolean;
  /** The user's default home view (`default_home_view`), null = the workspace default. */
  defaultHomeView: string | null;
  fontSize: FontSize;
}

export const PREFERENCE_DEFAULTS: Readonly<Preferences> = Object.freeze({
  ...UI_PREF_DEFAULTS,
  showFullUserNames: true,
  autoAssignToSelf: false,
  defaultHomeView: null,
  fontSize: 'default',
});

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The UI-pref half of a `ui_prefs` value: each key parsed on its own, a malformed one falls back to its default. */
export function readUiPrefs(uiPrefs: unknown): UiPrefPreferences {
  const src = isObj(uiPrefs) ? uiPrefs : {};
  const out = { ...UI_PREF_DEFAULTS } as Record<UiPrefKey, unknown>;
  for (const k of UI_PREF_KEYS) {
    if (!Object.hasOwn(src, k)) continue;
    const r = UI_PREF_SCHEMAS[k].safeParse(src[k]);
    if (r.success) out[k] = r.data;
  }
  return out as unknown as UiPrefPreferences;
}

/** A `UserSettings` row (wire names) → resolved preferences; null/undefined (no row yet) → the defaults. */
export function readPreferences(row: Readonly<Record<string, unknown>> | null | undefined): Preferences {
  if (!row) return { ...PREFERENCE_DEFAULTS };
  const fontSize = row.fontSize === 'small' || row.fontSize === 'large' ? row.fontSize : 'default';
  return {
    ...readUiPrefs(row.uiPrefs),
    showFullUserNames: typeof row.showFullUserNames === 'boolean' ? row.showFullUserNames : PREFERENCE_DEFAULTS.showFullUserNames,
    autoAssignToSelf: row.autoAssignToSelf === true,
    defaultHomeView: typeof row.defaultHomeView === 'string' && row.defaultHomeView !== '' ? row.defaultHomeView : null,
    fontSize,
  };
}

/** Changed preference keys between two `ui_prefs` values whose new value is not valid (server validation). */
export function invalidUiPrefChanges(prev: unknown, next: unknown): UiPrefKey[] {
  const a = isObj(prev) ? prev : {};
  const b = isObj(next) ? next : {};
  const bad: UiPrefKey[] = [];
  for (const k of UI_PREF_KEYS) {
    if (!Object.hasOwn(b, k)) continue;
    if (Object.hasOwn(a, k) && JSON.stringify(a[k]) === JSON.stringify(b[k])) continue;
    if (!UI_PREF_SCHEMAS[k].safeParse(b[k]).success) bad.push(k);
  }
  return bad;
}

// ---------------------------------------------------------------------------------------------------------------
// default home view values

/**
 * A home view value is either a static target id (`my-issues`, `inbox`, or another nav entry's `homeTarget.id`) or
 * a parameterized target `<kind>:<uuid>` — a team-scoped target such as `current-cycle:<teamId>`, or an entity
 * (`view:<customViewId>`, `project:<projectId>`, once favorites ship). The workspace column's legacy spelling
 * `myIssues` is read as `my-issues`.
 */
export const HOME_ENTITY_KINDS = ['view', 'project'] as const;
export type HomeEntityKind = (typeof HOME_ENTITY_KINDS)[number];

export type HomeViewRef =
  | { readonly kind: 'static'; readonly id: string }
  | { readonly kind: 'team'; readonly target: string; readonly teamId: string }
  | { readonly kind: 'entity'; readonly entity: HomeEntityKind; readonly id: string };

const STATIC_RE = /^[a-z][a-z0-9-]{0,39}$/;
const PARAM_RE = /^([a-z][a-z0-9-]{0,39}):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
/** Team-scoped home targets registered by nav entries in the team section. */
export const HOME_TEAM_TARGETS = ['current-cycle', 'team-issues'] as const;

export const DEFAULT_HOME_VIEW = 'my-issues';

export function normalizeHomeView(v: string): string {
  return v === 'myIssues' ? 'my-issues' : v;
}

/** Parse a stored or submitted home view; null when malformed. */
export function parseHomeView(value: string | null | undefined): HomeViewRef | null {
  if (typeof value !== 'string') return null;
  const v = normalizeHomeView(value);
  if (STATIC_RE.test(v)) return { kind: 'static', id: v };
  const m = PARAM_RE.exec(v);
  if (!m) return null;
  const [, kind, id] = m as unknown as [string, string, string];
  if ((HOME_ENTITY_KINDS as readonly string[]).includes(kind)) return { kind: 'entity', entity: kind as HomeEntityKind, id };
  if ((HOME_TEAM_TARGETS as readonly string[]).includes(kind)) return { kind: 'team', target: kind, teamId: id };
  return null;
}

/** The stored value of a home view ref. */
export function formatHomeView(ref: HomeViewRef): string {
  switch (ref.kind) {
    case 'static':
      return ref.id;
    case 'team':
      return `${ref.target}:${ref.teamId}`;
    case 'entity':
      return `${ref.entity}:${ref.id}`;
  }
}
